import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorMode,
  VStack,
  Alert,
  AlertIcon,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import config from "../config";

const LoginForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      console.log("API Base URL:", config.API_BASE_URL);
      const response = await fetch(
        `${config.API_BASE_URL}/accounts/auth/token/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "include",
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        throw new Error(t("auth.login.error"));
      }

      const data = await response.json();

      // Save tokens to localStorage with correct keys
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);

      // Redirect to home page or dashboard
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="md" py={10}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading
            size="xl"
            color={colorMode === "dark" ? "white" : "gray.800"}
            mb={2}
          >
            {t("auth.login.title")}
          </Heading>
          <Text color={colorMode === "dark" ? "gray.300" : "gray.600"}>
            {t("auth.login.subtitle")}
          </Text>
        </Box>

        <Box
          as="form"
          onSubmit={handleSubmit}
          bg={colorMode === "dark" ? "gray.700" : "white"}
          p={8}
          borderRadius="lg"
          boxShadow="md"
        >
          <Stack spacing={6}>
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <FormControl>
              <FormLabel color={colorMode === "dark" ? "gray.300" : "gray.600"}>
                {t("auth.login.username")}
              </FormLabel>
              <Input
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                placeholder={t("auth.login.usernamePlaceholder")}
                bg={colorMode === "dark" ? "gray.600" : "white"}
                borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                _hover={{
                  borderColor: colorMode === "dark" ? "blue.300" : "blue.500",
                }}
                _focus={{
                  borderColor: colorMode === "dark" ? "blue.300" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={colorMode === "dark" ? "gray.300" : "gray.600"}>
                {t("auth.login.password")}
              </FormLabel>
              <Input
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                placeholder={t("auth.login.passwordPlaceholder")}
                bg={colorMode === "dark" ? "gray.600" : "white"}
                borderColor={colorMode === "dark" ? "gray.500" : "gray.200"}
                _hover={{
                  borderColor: colorMode === "dark" ? "blue.300" : "blue.500",
                }}
                _focus={{
                  borderColor: colorMode === "dark" ? "blue.300" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              fontSize="md"
              isLoading={isLoading}
              loadingText={t("auth.login.loading")}
            >
              {t("auth.login.submit")}
            </Button>
          </Stack>
        </Box>
      </VStack>
    </Container>
  );
};

export default LoginForm;
